import React, { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CodeIcon from '@mui/icons-material/Code';
import CommentIcon from '@mui/icons-material/Comment';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CodeExplainer from './CodeExplainer';
import AIChatAssistant from './AIChatAssistant';
import './CodeSnippetViewer.css';

const CodeSnippetViewer = ({ snippet }) => {
  const [tabValue, setTabValue] = useState(0);
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  if (!snippet) {
    return null;
  }

  const hasExplanations = snippet.explanations && snippet.explanations.length > 0;
  const codeLines = snippet.code.split('\n');

  // Create code with inline comments
  const generateCodeWithComments = () => {
    if (!hasExplanations) return snippet.code;

    const explanationMap = {};
    snippet.explanations.forEach(exp => {
      explanationMap[exp.lineNumber] = exp;
    });

    let result = '';
    codeLines.forEach((line, index) => {
      const lineNumber = index + 1;
      const explanation = explanationMap[lineNumber];
      
      if (explanation) {
        // Add explanation as a comment before the line
        const commentedExplanation = formatCommentForLanguage(
          explanation.explanation,
          snippet.programmingLanguage
        );
        result += commentedExplanation + '\n';
      }
      
      result += line + '\n';
    });

    return result;
  };

  const formatCommentForLanguage = (text, language) => {
    const lines = text.split(/(?<=[.!?])\s+/); // Split by sentences
    let comment = '';
    
    const singleLineComment = getCommentStyle(language).single;
    const multiLineStart = getCommentStyle(language).multiStart;
    const multiLineEnd = getCommentStyle(language).multiEnd;
    
    if (lines.length === 1) {
      comment = `${singleLineComment} ${text}`;
    } else {
      comment = `${multiLineStart}\n`;
      lines.forEach(line => {
        comment += ` * ${line.trim()}\n`;
      });
      comment += ` ${multiLineEnd}`;
    }
    
    return comment;
  };

  const getCommentStyle = (language) => {
    switch (language) {
      case 'python':
        return { single: '#', multiStart: '"""', multiEnd: '"""' };
      case 'html':
        return { single: '<!--', multiStart: '<!--', multiEnd: '-->' };
      case 'css':
        return { single: '/*', multiStart: '/*', multiEnd: '*/' };
      default:
        return { single: '//', multiStart: '/*', multiEnd: '*/' };
    }
  };

  const getSyntaxHighlighterLanguage = (lang) => {
    const languageMap = {
      'javascript': 'javascript',
      'typescript': 'typescript',
      'python': 'python',
      'java': 'java',
      'html': 'markup',
      'css': 'css',
      'sql': 'sql',
      'csharp': 'csharp',
      'cpp': 'cpp',
      'php': 'php',
      'ruby': 'ruby',
      'go': 'go',
      'rust': 'rust',
      'kotlin': 'kotlin',
      'swift': 'swift'
    };
    return languageMap[lang] || 'javascript';
  };

  const handleCopy = async () => {
    try {
      const textToCopy = tabValue === 0 ? snippet.code : generateCodeWithComments();
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="code-snippet-viewer">
      {/* Snippet Header */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h2 style={{ margin: 0, fontSize: '24px' }}>{snippet.name}</h2>
              {snippet.snippetCode && (
                <span style={{
                  padding: '4px 12px',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  letterSpacing: '1px',
                  border: '1px solid #ddd'
                }}>
                  #{snippet.snippetCode}
                </span>
              )}
            </div>
            {snippet.description && (
              <p style={{ color: '#718096', fontSize: '14px', margin: '0 0 8px 0' }}>
                {snippet.description}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{
              padding: '4px 12px',
              backgroundColor: '#667eea',
              color: 'white',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {snippet.programmingLanguage.toUpperCase()}
            </span>
            <span style={{
              padding: '4px 12px',
              backgroundColor: snippet.difficulty === 'beginner' ? '#48bb78' :
                              snippet.difficulty === 'intermediate' ? '#4299e1' :
                              snippet.difficulty === 'advanced' ? '#ed8936' : '#f56565',
              color: 'white',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {snippet.difficulty}
            </span>
          </div>
        </div>

        {snippet.tags && snippet.tags.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {snippet.tags.map((tag, index) => (
              <span key={index} style={{
                padding: '4px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                fontSize: '12px',
                color: '#4a5568'
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Code Display */}
      <div className="card">
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex',
            gap: '4px'
          }}>
          <button
            onClick={() => setTabValue(0)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderBottom: tabValue === 0 ? '2px solid #667eea' : '2px solid transparent',
              backgroundColor: 'transparent',
              color: tabValue === 0 ? '#667eea' : '#718096',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
          >
            <CodeIcon fontSize="small" />
            Code Only
          </button>
          <button
            onClick={() => setTabValue(1)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderBottom: tabValue === 1 ? '2px solid #667eea' : '2px solid transparent',
              backgroundColor: 'transparent',
              color: tabValue === 1 ? '#667eea' : '#718096',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
          >
            <TipsAndUpdatesIcon fontSize="small" />
            Code Analysis
          </button>
          {hasExplanations && (
            <button
              onClick={() => setTabValue(2)}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderBottom: tabValue === 2 ? '2px solid #667eea' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: tabValue === 2 ? '#667eea' : '#718096',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                marginBottom: '-2px'
              }}
            >
              <CommentIcon fontSize="small" />
              Code with Explanations
            </button>
          )}
          {hasExplanations && (
            <button
              onClick={() => setTabValue(3)}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderBottom: tabValue === 3 ? '2px solid #667eea' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: tabValue === 3 ? '#667eea' : '#718096',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                marginBottom: '-2px'
              }}
            >
              <LightbulbIcon fontSize="small" />
              Line-by-Line Details
            </button>
          )}
          {hasExplanations && (
            <button
              onClick={() => setTabValue(4)}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderBottom: tabValue === 4 ? '2px solid #667eea' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: tabValue === 4 ? '#667eea' : '#718096',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                marginBottom: '-2px'
              }}
            >
              <SlideshowIcon fontSize="small" />
              Interactive Explainer
            </button>
          )}
          </div>
          
          {/* AI Chat Button */}
          <button
            onClick={() => setIsChatbotOpen(!isChatbotOpen)}
            style={{
              padding: '8px 16px',
              margin: '8px 12px',
              border: isChatbotOpen ? '2px solid #667eea' : '1px solid #e2e8f0',
              borderRadius: '8px',
              backgroundColor: isChatbotOpen ? '#667eea' : 'white',
              color: isChatbotOpen ? 'white' : '#667eea',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: isChatbotOpen ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none'
            }}
            title="Ask AI about this code (Ctrl+Shift+O)"
          >
            <SmartToyIcon fontSize="small" />
            AI Assistant
          </button>
        </div>

        <div style={{ padding: tabValue === 4 ? '0' : '20px' }}>
          {/* Tab 0: Code Only */}
          {tabValue === 0 && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={handleCopy}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '8px',
                  zIndex: 1,
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </button>
              <SyntaxHighlighter
                language={getSyntaxHighlighterLanguage(snippet.programmingLanguage)}
                style={theme === 'dark' ? vscDarkPlus : oneLight}
                showLineNumbers
                wrapLines
                customStyle={{
                  margin: 0,
                  borderRadius: '8px',
                  fontSize: '18px'
                }}
              >
                {snippet.code}
              </SyntaxHighlighter>
            </div>
          )}

          {/* Tab 1: Code Analysis */}
          {tabValue === 1 && snippet.aiAnalysis && (
            <div>
              {snippet.aiAnalysis.overallPurpose && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ color: '#667eea', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                    Overall Purpose
                  </h4>
                  <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#2d3748', margin: 0 }}>
                    {snippet.aiAnalysis.overallPurpose}
                  </p>
                </div>
              )}

              {snippet.aiAnalysis.keyTechniques && snippet.aiAnalysis.keyTechniques.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ color: '#667eea', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                    Key Techniques
                  </h4>
                  <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
                    {snippet.aiAnalysis.keyTechniques.map((technique, idx) => (
                      <li key={idx} style={{ fontSize: '14px', marginBottom: '4px', color: '#2d3748' }}>
                        {technique}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {snippet.aiAnalysis.bestPractices && snippet.aiAnalysis.bestPractices.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ color: '#48bb78', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                    Best Practices Demonstrated
                  </h4>
                  <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
                    {snippet.aiAnalysis.bestPractices.map((practice, idx) => (
                      <li key={idx} style={{ fontSize: '14px', marginBottom: '4px', color: '#2d3748' }}>
                        {practice}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {snippet.aiAnalysis.potentialImprovements && snippet.aiAnalysis.potentialImprovements.length > 0 && (
                <div>
                  <h4 style={{ color: '#ed8936', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                    Potential Improvements
                  </h4>
                  <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
                    {snippet.aiAnalysis.potentialImprovements.map((improvement, idx) => (
                      <li key={idx} style={{ fontSize: '14px', marginBottom: '4px', color: '#2d3748' }}>
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Code with Explanations */}
          {tabValue === 2 && hasExplanations && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={handleCopy}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '8px',
                  zIndex: 1,
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </button>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '6px',
                color: '#01579b',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                Explanations are shown as comments above each line of code
              </div>
              <SyntaxHighlighter
                language={getSyntaxHighlighterLanguage(snippet.programmingLanguage)}
                style={theme === 'dark' ? vscDarkPlus : oneLight}
                showLineNumbers
                wrapLines
                customStyle={{
                  margin: 0,
                  borderRadius: '8px',
                  fontSize: '18px'
                }}
              >
                {generateCodeWithComments()}
              </SyntaxHighlighter>
            </div>
          )}

          {/* Tab 3: Line-by-Line Details */}
          {tabValue === 3 && hasExplanations && (
            <div>
              {snippet.explanations.map((exp, index) => (
                <div key={index} style={{
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#f7fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    <span style={{
                      padding: '4px 12px',
                      backgroundColor: '#667eea',
                      color: 'white',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: '500',
                      minWidth: '70px',
                      textAlign: 'center'
                    }}>
                      Line {exp.lineNumber}
                    </span>
                    <div style={{ flex: 1 }}>
                      <SyntaxHighlighter
                        language={getSyntaxHighlighterLanguage(snippet.programmingLanguage)}
                        style={oneLight}
                        customStyle={{
                          margin: 0,
                          padding: '8px',
                          fontSize: '16px',
                          borderRadius: '4px'
                        }}
                      >
                        {exp.lineCode}
                      </SyntaxHighlighter>
                      <p style={{
                        marginTop: '8px',
                        fontSize: '14px',
                        lineHeight: '1.7',
                        color: '#2d3748'
                      }}>
                        {exp.explanation}
                      </p>
                      {exp.keyPoints && exp.keyPoints.length > 0 && (
                        <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {exp.keyPoints.map((point, idx) => (
                            <span key={idx} style={{
                              padding: '4px 12px',
                              border: '1px solid #b794f4',
                              color: '#6b46c1',
                              borderRadius: '16px',
                              fontSize: '12px'
                            }}>
                              {point}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tab 4: Interactive Explainer */}
          {tabValue === 4 && hasExplanations && (
            <CodeExplainer snippet={snippet} />
          )}

          {!hasExplanations && tabValue > 1 && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fbbf24',
              borderRadius: '6px',
              color: '#92400e',
              fontSize: '14px'
            }}>
              No AI explanations available for this snippet. They may be generating or need to be created.
            </div>
          )}
        </div>
      </div>



      {/* AI Chat Assistant */}
      {snippet && (
        <AIChatAssistant 
          assetId={snippet._id}
          chapterId={snippet._id}
          isDarkMode={theme === 'dark'}
          isOpen={isChatbotOpen}
          onOpenChange={setIsChatbotOpen}
          assignmentContext={{
            snippetId: snippet._id,
            snippetName: snippet.name,
            programmingLanguage: snippet.programmingLanguage,
            code: snippet.code,
            description: snippet.description,
            difficulty: snippet.difficulty,
            tags: snippet.tags,
            hasExplanations: hasExplanations,
            currentTab: tabValue === 0 ? 'Code Only' : 
                        tabValue === 1 ? 'Code Analysis' : 
                        tabValue === 2 ? 'With Comments' : 
                        tabValue === 3 ? 'Line-by-Line' : 
                        'Interactive Explainer'
          }}
        />
      )}
    </div>
  );
};

export default CodeSnippetViewer;
